import axios from 'axios';
import { apiUrl, getAuthHeaders } from '../api/network_calls';

export const TERMS_VERSION = '2026-10-29';
export type TermsReceipt = { sub: string; version: string; acceptedAt: string; requestId: string; recorded: true; authHint?: string };
export const deviceTermsKey = (sub: string) => `${apiUrl('/signup-terms')}:${sub}`;
export const hasDeviceTerms = (receipt: TermsReceipt | undefined, sub: string) =>
  receipt?.recorded === true && receipt.sub === sub && receipt.version === TERMS_VERSION;

// Bound the whole save, including authentication headers, so an outage cannot trap login.
export const TERMS_SAVE_TIMEOUT_MS = 5000;
export async function recordDeviceTerms(sub: string, requestId: string, version = TERMS_VERSION): Promise<TermsReceipt> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const save = async () => {
    const headers = await getAuthHeaders();
    if (controller.signal.aborted) throw new Error('Terms acceptance timed out.');
    const { data } = await axios.put(apiUrl('/signup-terms'), {
      method: 'explicit_modal', accepted: true, version, source: 'web', requestId,
    }, { headers, timeout: TERMS_SAVE_TIMEOUT_MS, signal: controller.signal });
    if (data?.recorded !== true || data.sub !== sub || data.version !== version || data.requestId !== requestId || !Number.isFinite(Date.parse(data.acceptedAt))) {
      throw new Error('Terms acceptance was not confirmed.');
    }
    return data;
  };
  try {
    return await Promise.race([
      save(),
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => {
          reject(new Error('Terms acceptance timed out.'));
          controller.abort();
        }, TERMS_SAVE_TIMEOUT_MS);
      }),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

export type TermsStatus = {
  sub: string; version: string; accepted: boolean; acceptedAt: string | null;
  checkedAt: number; clientVersion: string;
};
export type TermsCacheEntry = TermsReceipt | TermsStatus;
export const TERMS_STATUS_TTL_MS = 24 * 60 * 60 * 1000;
export function freshTermsStatus(entry: TermsCacheEntry | undefined, sub: string, now = Date.now()): entry is TermsStatus {
  return !!entry && 'checkedAt' in entry && entry.sub === sub && entry.clientVersion === TERMS_VERSION
    && typeof entry.accepted === 'boolean' && typeof entry.version === 'string' && !!entry.version
    && Number.isFinite(entry.checkedAt) && now >= entry.checkedAt && now - entry.checkedAt < TERMS_STATUS_TTL_MS;
}
export function acceptedTermsStatus(receipt: TermsReceipt): TermsStatus {
  return { sub: receipt.sub, version: receipt.version, accepted: true, acceptedAt: receipt.acceptedAt,
    checkedAt: Date.now(), clientVersion: TERMS_VERSION };
}
const pendingStatus = new Map<string, Promise<TermsStatus>>();
/** Coalesce concurrent mounts; only a validated API response becomes a daily cache entry. */
export function fetchTermsStatus(sub: string): Promise<TermsStatus> {
  const key = deviceTermsKey(sub);
  const pending = pendingStatus.get(key);
  if (pending) return pending;
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout>;
  const read = async () => {
    const headers = await getAuthHeaders();
    if (controller.signal.aborted) throw new Error('Terms status timed out.');
    const { data } = await axios.get(apiUrl('/signup-terms'), { headers, signal: controller.signal, timeout: TERMS_SAVE_TIMEOUT_MS });
    if (data?.sub !== sub || typeof data.accepted !== 'boolean' || typeof data.version !== 'string' || !data.version
      || (data.accepted && !Number.isFinite(Date.parse(data.acceptedAt)))) throw new Error('Invalid terms status.');
    return { sub, version: data.version, accepted: data.accepted, acceptedAt: data.accepted ? data.acceptedAt : null,
      checkedAt: Date.now(), clientVersion: TERMS_VERSION };
  };
  const request = Promise.race([read(), new Promise<never>((_, reject) => {
    timer = setTimeout(() => { controller.abort(); reject(new Error('Terms status timed out.')); }, TERMS_SAVE_TIMEOUT_MS);
  })]).finally(() => { clearTimeout(timer); pendingStatus.delete(key); });
  pendingStatus.set(key, request);
  return request;
}
